import {
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameDay, isSameMonth, differenceInCalendarDays, startOfDay,
} from "date-fns";
import type { CalendarEvent } from "@workspace/api-client-react";
import type { TaskItem, TaskTimeBlock, TaskTag, Folder } from "./types";
import { TASK_STATUSES } from "./types";

export type CalendarView = "month" | "week" | "day" | "agenda";

/**
 * One task's working period, placed on the calendar — a task with 3 blocks
 * produces 3 of these, each independently clickable/draggable but all
 * opening the same task when clicked.
 */
export interface CalendarTask {
  kind: "task";
  task: TaskItem;
  block: TaskTimeBlock;
  start: Date;
  end: Date;
  /** Spans one or more full days with no meaningful time-of-day component. */
  isAllDay: boolean;
}

/**
 * A synced Google Calendar event, placed on the calendar the same way a
 * CalendarTask is — read-only for now (no drag-to-reschedule, no done
 * toggle; those are meaningless without a task behind it), so it carries
 * only what rendering needs, not the editing affordances tasks have.
 */
export interface CalendarGoogleEvent {
  kind: "event";
  event: CalendarEvent;
  start: Date;
  end: Date;
  isAllDay: boolean;
  /** The signed-in user hasn't accepted this invite yet — rendered with a dashed outline instead of a solid fill. */
  isUnconfirmed: boolean;
}

/**
 * Everything placeable on the calendar grid. The layout functions below
 * (tasksOnDay, layoutOverlappingEvents, layoutMonthWeek, isMultiDay) only
 * ever read `.start`/`.end`/`.isAllDay`, so they're generic over this union
 * without any logic changes — only the render layer (CalendarPane.tsx,
 * CalendarTimeGrid.tsx) needs to branch on `.kind`.
 */
export type CalendarEntry = CalendarTask | CalendarGoogleEvent;

// Where a calendar item actually came from — every entry has exactly one,
// so this drives the small source badge shown on every calendar item.
// "ado" has no task.source value of "ado" wired up from anywhere yet, but is
// named here so the badge is ready the moment it lands rather than needing a
// second pass.
export type CalendarSource = "google" | "outlook" | "jira" | "ado" | "native";

export function sourceForEntry(entry: CalendarEntry): CalendarSource {
  if (entry.kind === "event") return entry.event.source;
  if (entry.task.source === "jira") return "jira";
  if (entry.task.source === "ado") return "ado";
  return "native";
}

// Treat an exact midnight-to-midnight (or same-instant) span as all-day — the
// natural way a task created via a Month-view day click (no specific time)
// ends up represented, same convention Google Calendar uses for date-only
// events versus timed ones.
function computeIsAllDay(start: Date, end: Date): boolean {
  const startsAtMidnight = start.getHours() === 0 && start.getMinutes() === 0;
  const spansFullDays = differenceInCalendarDays(end, start) >= 1;
  return startsAtMidnight && (spansFullDays || (end.getHours() === 0 && end.getMinutes() === 0));
}

/**
 * Flattens every task's working periods into individually-placeable calendar
 * entries. `timeBlocksByTaskId` groups the globally-fetched block list by
 * task — same shape as `subtasksByParent` in TaskTable.tsx.
 */
export function toCalendarTasks(tasks: TaskItem[], timeBlocksByTaskId: Map<number, TaskTimeBlock[]>): CalendarTask[] {
  const result: CalendarTask[] = [];
  for (const task of tasks) {
    for (const block of timeBlocksByTaskId.get(task.id) ?? []) {
      const start = new Date(block.startDate);
      const end = new Date(block.endDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) continue;
      // The explicit flag is authoritative; the exact-midnight-span inference
      // stays as a fallback so a period created before this flag existed (or
      // any built via raw date math without setting it) still renders correctly.
      result.push({ kind: "task", task, block, start, end, isAllDay: block.isAllDay || computeIsAllDay(start, end) });
    }
  }
  return result;
}

/**
 * Same idea as toCalendarTasks, for synced calendar events (Google or
 * Outlook). Whichever connected-account email matches the event's own
 * `source` is looked up in its attendee list — Google/Outlook auto-create
 * this event the moment an invite arrives, with the signed-in user's own
 * RSVP starting at "needsAction" (Google) / "notResponded" (Outlook) until
 * they respond, which is what "unconfirmed" means here.
 */
export function toCalendarEventEntries(
  events: CalendarEvent[],
  selfGoogleEmail?: string | null,
  selfOutlookEmail?: string | null,
): CalendarGoogleEvent[] {
  const result: CalendarGoogleEvent[] = [];
  for (const event of events) {
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) continue;
    const selfEmail = event.source === "google" ? selfGoogleEmail : selfOutlookEmail;
    const selfAttendee = selfEmail
      ? event.attendees.find((a) => a.email.toLowerCase() === selfEmail.toLowerCase())
      : undefined;
    const isUnconfirmed = selfAttendee?.responseStatus === "needsAction" || selfAttendee?.responseStatus === "notResponded";
    result.push({ kind: "event", event, start, end, isAllDay: event.isAllDay, isUnconfirmed });
  }
  return result;
}

/**
 * The date grid for month view, including leading/trailing adjacent-month
 * days — 7 columns per week normally, or 5 (Mon-Fri) with weekends excluded.
 */
export function getMonthGrid(monthDate: Date, weekStartsOn: 0 | 1 = 0, includeWeekends = true): Date[] {
  const start = startOfWeek(startOfMonth(monthDate), { weekStartsOn });
  const end = endOfWeek(endOfMonth(monthDate), { weekStartsOn });
  const days = eachDayOfInterval({ start, end });
  return includeWeekends ? days : days.filter((d) => d.getDay() !== 0 && d.getDay() !== 6);
}

/** The days of the week containing weekDate — 7, or 5 (Mon-Fri) with weekends excluded. */
export function getWeekDays(weekDate: Date, weekStartsOn: 0 | 1 = 0, includeWeekends = true): Date[] {
  const days = eachDayOfInterval({ start: startOfWeek(weekDate, { weekStartsOn }), end: endOfWeek(weekDate, { weekStartsOn }) });
  return includeWeekends ? days : days.filter((d) => d.getDay() !== 0 && d.getDay() !== 6);
}

export type CalendarColorBy = "priority" | "list" | "status";

/** Resolves which palette key (see TAG_SWATCH_STYLES) a task should render in, per the calendar's "color by" setting. */
export function colorKeyForTask(task: TaskItem, colorBy: CalendarColorBy, tags: TaskTag[], folders: Folder[]): string {
  if (colorBy === "status") {
    return TASK_STATUSES.find((s) => s.key === task.status)?.color ?? "gray";
  }
  if (colorBy === "list") {
    const list = folders.find((f) => f.kind === "list" && Number(f.key.slice("list:".length)) === task.listId);
    return list?.color ?? "gray";
  }
  const tag = tags.find((t) => t.id === task.priorityTagId);
  return tag?.color ?? "gray";
}

// Small display-label lookups backing the calendar's "show on item" settings
// (name is always shown; list/priority/status are each independently
// toggleable) — shared between the Month/Agenda views (CalendarPane.tsx) and
// the Week/Day time grid (CalendarTimeGrid.tsx).
export function listLabelForTask(task: TaskItem, folders: Folder[]): string | undefined {
  return folders.find((f) => f.kind === "list" && Number(f.key.slice("list:".length)) === task.listId)?.label;
}

export function priorityLabelForTask(task: TaskItem, tags: TaskTag[]): string | undefined {
  return tags.find((t) => t.id === task.priorityTagId)?.name;
}

export function statusLabelForTask(task: TaskItem): string | undefined {
  return TASK_STATUSES.find((s) => s.key === task.status)?.label;
}

// TAG_COLOR_KEYS palette keys (see types.ts) for the same three fields —
// lets calendar item badges match the color-coding used everywhere else a
// list/priority/status shows up (folder nav dots, TagPill, StatusPill).
export function listColorForTask(task: TaskItem, folders: Folder[]): string {
  return folders.find((f) => f.kind === "list" && Number(f.key.slice("list:".length)) === task.listId)?.color ?? "gray";
}

export function priorityColorForTask(task: TaskItem, tags: TaskTag[]): string {
  return tags.find((t) => t.id === task.priorityTagId)?.color ?? "gray";
}

export function statusColorForTask(task: TaskItem): string {
  return TASK_STATUSES.find((s) => s.key === task.status)?.color ?? "gray";
}

/** [startHour, endHour] per day of week, indexed by Date#getDay() (0 = Sunday). */
export type WorkHoursByDay = [number, number][];

export const DEFAULT_WORK_HOURS_BY_DAY: WorkHoursByDay = [
  [9, 17], // Sun
  [8, 17], // Mon
  [8, 17], // Tue
  [8, 17], // Wed
  [8, 17], // Thu
  [8, 17], // Fri
  [9, 17], // Sat
];

/** The combined [startHour, endHour] band spanning every visible day's configured working hours — used to center the time grid. */
export function workHoursBandForDays(days: Date[], workHoursByDay: WorkHoursByDay): [number, number] {
  const bands = days.map((d) => workHoursByDay[d.getDay()] ?? DEFAULT_WORK_HOURS_BY_DAY[d.getDay()]!);
  return [Math.min(...bands.map((b) => b[0])), Math.max(...bands.map((b) => b[1]))];
}

export function isInMonth(day: Date, monthDate: Date): boolean {
  return isSameMonth(day, monthDate);
}

/** Calendar entries whose span overlaps the given day at all (for month-view chips). */
export function tasksOnDay(calTasks: CalendarEntry[], day: Date): CalendarEntry[] {
  const dayStart = startOfDay(day);
  return calTasks.filter((ct) => {
    const s = startOfDay(ct.start);
    const e = startOfDay(ct.end);
    return dayStart >= s && dayStart <= e;
  });
}

/** True if an entry's span covers more than one calendar day. */
export function isMultiDay(ct: CalendarEntry): boolean {
  return differenceInCalendarDays(startOfDay(ct.end), startOfDay(ct.start)) > 0;
}

export interface PositionedTask {
  calTask: CalendarEntry;
  /** 0-based column index among concurrently-overlapping tasks that day. */
  column: number;
  /** Total number of overlapping columns for this task's cluster — column widths divide evenly by this. */
  columnCount: number;
}

/**
 * Side-by-side column layout for timed (non-all-day) entries on a single day
 * in the week/day time-grid — the one genuinely new layout problem this
 * feature needs (month view just lists tasks vertically, no overlap layout
 * required). A simple greedy interval-graph coloring: sort by start time,
 * assign each entry the first column whose previous occupant has already ended.
 */
export function layoutOverlappingEvents(dayTasks: CalendarEntry[]): PositionedTask[] {
  const timed = [...dayTasks].filter((ct) => !ct.isAllDay).sort((a, b) => a.start.getTime() - b.start.getTime());
  const columnEnds: Date[] = []; // columnEnds[i] = end time of the last task placed in column i
  const placements: { calTask: CalendarEntry; column: number }[] = [];

  for (const ct of timed) {
    let column = columnEnds.findIndex((end) => end <= ct.start);
    if (column === -1) {
      column = columnEnds.length;
      columnEnds.push(ct.end);
    } else {
      columnEnds[column] = ct.end;
    }
    placements.push({ calTask: ct, column });
  }

  // Overlap clusters can have different widths, but a single columnCount
  // (the max concurrent overlap) keeps every column in this day a consistent
  // width — simpler and visually cleaner than per-cluster width, at the cost
  // of some unused space next to a task that only overlaps briefly.
  const columnCount = Math.max(1, columnEnds.length);
  return placements.map((p) => ({ ...p, columnCount }));
}

export interface MonthWeekBar {
  calTask: CalendarEntry;
  /** 0-6 column within the week row, clipped to the row's bounds. */
  colStart: number;
  colEnd: number;
  lane: number;
}

/**
 * Lays out one week-row of month view: every entry touching that row becomes
 * a bar spanning its start/end column (clipped to the row), stacked into
 * lanes via the same greedy interval-placement idea as
 * layoutOverlappingEvents, just keyed by day-column range instead of time.
 * A single-day entry is just a bar with colStart === colEnd.
 */
export function layoutMonthWeek(weekDays: Date[], calTasks: CalendarEntry[]): MonthWeekBar[] {
  const weekStart = startOfDay(weekDays[0]!);
  const weekEnd = startOfDay(weekDays[weekDays.length - 1]!);
  const touching = calTasks.filter((ct) => startOfDay(ct.start) <= weekEnd && startOfDay(ct.end) >= weekStart);

  const sorted = [...touching].sort((a, b) => a.start.getTime() - b.start.getTime());
  const laneEnds: number[] = []; // laneEnds[i] = last occupied column in lane i
  const bars: MonthWeekBar[] = [];

  for (const ct of sorted) {
    const colStart = Math.max(0, differenceInCalendarDays(startOfDay(ct.start), weekStart));
    const colEnd = Math.min(6, differenceInCalendarDays(startOfDay(ct.end), weekStart));
    let lane = laneEnds.findIndex((end) => end < colStart);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(colEnd);
    } else {
      laneEnds[lane] = colEnd;
    }
    bars.push({ calTask: ct, colStart, colEnd, lane });
  }

  return bars;
}

export function formatRangeLabel(view: CalendarView, anchor: Date): string {
  const opts: Intl.DateTimeFormatOptions =
    view === "month" ? { month: "long", year: "numeric" }
    : view === "day" ? { month: "long", day: "numeric", year: "numeric" }
    : { month: "long", year: "numeric" }; // week/agenda: month context is enough
  return anchor.toLocaleDateString(undefined, opts);
}

export { isSameDay };
